import { type SuretyAccountId, SuretyQuoteStatus, suretyQuoteStatusDef } from "@inrev/common";
import { useAdminFetchAccountBondRequestPreviews } from "@portal/api/bond-request/admin";
import type { BondRequestPreview } from "@portal/api/bond-request/agent/types";
import {
	DynamicDataTable,
	type DynamicDataTableColumn,
} from "@portal/components/ui/DynamicDataTable/DynamicDataTable";
import { ErrorDisplay } from "@portal/components/ui/ErrorDisplay";
import { RowActionButtons } from "@portal/components/ui/row-action-buttons/RowActionButtons";
import { getStatusTagByStatus } from "@portal/components/ui/StatusTag";
import { useIsMinimalBondUIEnabled } from "@portal/hooks/useIsMinimalBondUIEnabled";
import { DateTime } from "luxon";
import { useMemo } from "react";
import { bondRequestTypeLabelMap } from "../../../../../constants";
import { formatBondAmount, formatDateForCsv, formatEmpty } from "../../../../../utils";
import { AdminAssigneeControls } from "../../../../admin/shared/AdminAssigneeControls";

const EMPTY_DATA: BondRequestPreview[] = [];
const INITIAL_SORTING = [{ id: "submittedAt", desc: true }];

const COLUMN_VISIBILITY = { entityTags: false };

const DEFAULT_STATUS_FILTERS = [
	{
		id: "status",
		value: suretyQuoteStatusDef.filter(
			(s) => s !== SuretyQuoteStatus.closed && s !== SuretyQuoteStatus.bound,
		),
	},
];

const BASE_COLUMNS: DynamicDataTableColumn<BondRequestPreview>[] = [
	{
		id: "underwriter",
		accessorKey: "underwriterUserId",
		size: 60,
		cell: ({ row }) =>
			row.original.underwriterUserId !== undefined ? (
				<RowActionButtons alwaysShow={true}>
					<AdminAssigneeControls
						assignedEntity={{ entityType: "quote", entityId: row.original.id }}
						currentAssignee={row.original.underwriterUserId}
					/>
				</RowActionButtons>
			) : null,
	},
	{
		id: "status",
		accessorKey: "status",
		header: "Status",
		size: 205,
		headerFilter: { type: "checkbox" },
		cell: ({ row }) => getStatusTagByStatus(row.original.status),
	},
	{
		id: "type",
		header: "Type",
		size: 180,
		minSize: 130,
		enableResizing: true,
		accessorFn: (row) =>
			formatEmpty(row.suretySubType ? bondRequestTypeLabelMap[row.suretySubType] : undefined),
	},
	{
		id: "principal_name",
		header: "Principal",
		size: 180,
		minSize: 130,
		enableResizing: true,
		accessorFn: (row) => formatEmpty(row.principalName),
	},
	{
		id: "amount",
		accessorKey: "amount",
		header: "Amount",
		size: 180,
		minSize: 130,
		cell: ({ row }) => formatBondAmount(row.original.amount),
	},
	{
		id: "description",
		header: "Description",
		size: 180,
		minSize: 130,
		enableResizing: true,
		accessorFn: (row) => formatEmpty(row.description),
	},
	{
		id: "submittedAt",
		header: "Submitted",
		accessorKey: "submittedAt",
		size: 150,
		enableSorting: true,
		cell: ({ row }) =>
			row.original.submittedAt !== undefined
				? DateTime.fromISO(row.original.submittedAt).toLocaleString(DateTime.DATE_MED)
				: "--",
		csv: {
			formatValue: formatDateForCsv,
		},
	},
	{
		id: "entityTags",
		header: "Tags",
		accessorFn: (row) => row.entityTags?.map((tag) => tag.displayName).join(", ") ?? "--",
	},
];

const getColumns = (isMinimalUIEnabled: boolean): DynamicDataTableColumn<BondRequestPreview>[] =>
	BASE_COLUMNS.map((column) =>
		column.id === "description"
			? {
					id: "description",
					header: isMinimalUIEnabled ? "Project Name" : "Description",
					size: 180,
					minSize: 130,
					enableResizing: true,
					accessorFn: (row: BondRequestPreview) =>
						formatEmpty(isMinimalUIEnabled ? row.name : row.description),
				}
			: column,
	);

type AdminAccountBondRequestsTableProps = {
	accountId: SuretyAccountId;
	searchString?: string;
};

export const AdminAccountBondRequestsTable = ({
	accountId,
	searchString,
}: AdminAccountBondRequestsTableProps) => {
	const {
		adminAccountBondRequestPreviews,
		adminAccountBondRequestPreviewsIsLoading,
		adminAccountBondRequestPreviewsError,
	} = useAdminFetchAccountBondRequestPreviews(accountId);
	const isMinimalUIEnabled = useIsMinimalBondUIEnabled();
	const columns = useMemo(() => getColumns(isMinimalUIEnabled), [isMinimalUIEnabled]);

	if (adminAccountBondRequestPreviewsError) {
		return <ErrorDisplay error={adminAccountBondRequestPreviewsError} />;
	}

	return (
		<DynamicDataTable
			name={`admin_account_requests`}
			columns={columns}
			data={adminAccountBondRequestPreviews ?? EMPTY_DATA}
			dataLoading={adminAccountBondRequestPreviewsIsLoading}
			initialSorting={INITIAL_SORTING}
			getRowHref={(row) => `/accounts/${row.accountId}/requests/${row.id}`}
			defaultColumnFilters={DEFAULT_STATUS_FILTERS}
			searchString={searchString}
			columnVisibility={COLUMN_VISIBILITY}
			enableExportToCSV
			tableClassName="shadow-sm rounded-xl"
			maxHeight={400}
			minHeight={400}
		/>
	);
};
